import { getContrast, parseToRgba, readableColor, rgba } from 'color2k';
import { isColor, parseLegacyColor } from './invertColors.js';

/**
 * @file ensureReadableText.js
 * Gives email text a readable color where the sender painted a background but
 * left the text color to us.
 *
 * Text that declares no color of its own takes the bubble's text color. That
 * is fine on our bubble, not on a background the sender declared: a Gmail
 * written in dark mode carries `background-color: rgb(18,19,24)` on every
 * paragraph and no text color at all, so our dark text lands on near-black in
 * the light theme, and in the dark theme the mirrored, near-white background
 * sits under our light text. Wherever our color meets the sender's background
 * with too little contrast, the element gets black or white explicitly.
 * Colors the sender chose are left as sent.
 */

/**
 * What the bubble typography paints text with: `--slate-12` and `--slate-11`
 * per theme from prose-bubble, and the plain prose heading color that
 * prose-bubble does not override.
 */
const THEME_TEXT_COLORS = {
  light: {
    body: 'rgb(28, 32, 36)',
    muted: 'rgb(96, 100, 108)',
    headings: 'rgb(17, 24, 39)',
  },
  dark: {
    body: 'rgb(237, 238, 240)',
    muted: 'rgb(176, 180, 186)',
    headings: 'rgb(17, 24, 39)',
  },
};

/**
 * Tags the bubble typography colors itself. They show our color even inside
 * an element the sender colored, since a class rule beats inheritance.
 */
const PROSE_TEXT_COLORS = {
  A: 'body',
  B: 'body',
  STRONG: 'body',
  H1: 'body',
  H2: 'body',
  H3: 'body',
  TH: 'body',
  BLOCKQUOTE: 'muted',
  CODE: 'muted',
  H4: 'headings',
  DT: 'headings',
};

/** Tags prose-bubble draws on a background of its own, our text on our color */
const PROSE_BACKGROUND_TAGS = new Set(['CODE', 'PRE']);

/**
 * Tags whose inline style survives the sanitizer inside <Letter>
 * (lettersanitizer's allowedTags that allow `style`). It unwraps every other
 * tag or strips its style, so what such a tag declares is never painted.
 */
const STYLED_TAGS = new Set([
  'A',
  'ADDRESS',
  'B',
  'BLOCKQUOTE',
  'BR',
  'CAPTION',
  'CENTER',
  'CITE',
  'CODE',
  'COL',
  'COLGROUP',
  'DD',
  'DIV',
  'DT',
  'EM',
  'FONT',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'I',
  'IMG',
  'LABEL',
  'LEGEND',
  'LI',
  'OL',
  'P',
  'PRE',
  'SPAN',
  'STRONG',
  'SUB',
  'SUP',
  'TABLE',
  'TBODY',
  'TD',
  'TFOOT',
  'TH',
  'THEAD',
  'TR',
  'U',
  'UL',
]);

/** Tags browsers paint a `bgcolor` on and the sanitizer keeps it for */
const BGCOLOR_TAGS = new Set(['TABLE', 'TFOOT', 'TR', 'TD', 'TH']);

/**
 * Below this our text color gets replaced. It is the WCAG minimum for large
 * text, and it only ever judges our color, never one the sender picked.
 */
const MIN_CONTRAST = 3;

/**
 * A background whose color under the text we cannot know: an image or a
 * gradient, or a translucent color over one of those or over our bubble, whose
 * color depends on the message.
 */
const UNKNOWN_BACKGROUND = Symbol('unknown background');

const isPaintedImage = value => /url\(|gradient\(/i.test(value);

/**
 * A `background` shorthand that sets a color or an image is expanded into its
 * longhands, so those are what is read.
 *
 * @param {Element} el - An element of the email
 * @returns {string|symbol|null} The background color the element declares,
 * UNKNOWN_BACKGROUND for an image, or null when it lets its parent show
 */
const declaredBackground = el => {
  if (!STYLED_TAGS.has(el.tagName)) return null;

  const { style } = el;
  if (isPaintedImage(style.getPropertyValue('background-image'))) {
    return UNKNOWN_BACKGROUND;
  }

  // The style wins over the presentational attribute, even when it paints
  // nothing (`background: none`, which not every engine expands)
  const inline = style.getPropertyValue('background-color');
  let color = null;
  if (inline || style.getPropertyValue('background')) {
    color = isColor(inline) ? inline : null;
  } else if (BGCOLOR_TAGS.has(el.tagName)) {
    color = parseLegacyColor(el.getAttribute('bgcolor') ?? '');
  }
  if (!color) return null;

  const [, , , alpha] = parseToRgba(color);
  return alpha > 0 ? color : null;
};

/**
 * The contrast helpers ignore alpha, so a translucent color is blended with
 * what shows through it first.
 *
 * @param {string|symbol} color - A background the element declares
 * @param {string|symbol|null} under - The background beneath it, null for ours
 * @returns {string|symbol} The opaque color painted, or UNKNOWN_BACKGROUND
 */
const paintedBackground = (color, under) => {
  if (color === UNKNOWN_BACKGROUND) return color;

  const [red, green, blue, alpha] = parseToRgba(color);
  if (alpha === 1) return color;
  if (!under || under === UNKNOWN_BACKGROUND) return UNKNOWN_BACKGROUND;

  const [underRed, underGreen, underBlue] = parseToRgba(under);
  const blend = (top, bottom) => Math.round(top * alpha + bottom * (1 - alpha));
  return rgba(
    blend(red, underRed),
    blend(green, underGreen),
    blend(blue, underBlue),
    1
  );
};

/**
 * @param {Element} el - An element of the email
 * @returns {string|null} The text color the sender gave the element
 */
const declaredTextColor = el => {
  if (!STYLED_TAGS.has(el.tagName)) return null;

  const color = el.style.getPropertyValue('color');
  if (isColor(color)) return color;
  return el.tagName === 'FONT'
    ? parseLegacyColor(el.getAttribute('color') ?? '')
    : null;
};

const fixTextColor = (el, inherited, themeColors) => {
  let { background, text } = inherited;

  const ownBackground = declaredBackground(el);
  if (ownBackground) background = paintedBackground(ownBackground, background);
  else if (PROSE_BACKGROUND_TAGS.has(el.tagName)) background = null;

  const ownText = declaredTextColor(el);
  const proseText = PROSE_TEXT_COLORS[el.tagName];
  if (ownText) text = { color: ownText, ours: false };
  else if (proseText) text = { color: themeColors[proseText], ours: true };

  if (
    text.ours &&
    background &&
    background !== UNKNOWN_BACKGROUND &&
    getContrast(text.color, background) < MIN_CONTRAST
  ) {
    const readable = readableColor(background);
    el.style.setProperty('color', readable);
    // Still ours: a descendant painting another background needs it checked
    text = { color: readable, ours: true };
  }

  Array.from(el.children).forEach(child =>
    fixTextColor(child, { background, text }, themeColors)
  );
};

/**
 * @param {string} html - Sanitized email HTML, already mirrored in dark mode
 * @param {{ isDark: boolean }} options - Whether the dark theme is applied
 * @returns {string} The same HTML, with a readable color wherever our text
 * color would sit on a background of the sender's that it blends into
 */
export const ensureReadableText = (html, { isDark = false } = {}) => {
  if (!html || typeof html !== 'string') return html;

  const themeColors = THEME_TEXT_COLORS[isDark ? 'dark' : 'light'];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  Array.from(doc.body.children).forEach(el =>
    fixTextColor(
      el,
      { background: null, text: { color: themeColors.body, ours: true } },
      themeColors
    )
  );

  return doc.body.innerHTML;
};

export default ensureReadableText;
